import React, { useRef, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useCompactLayout } from '../hooks/useCompactLayout';

interface CustomToggleProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: string | React.ReactNode;
    disabled?: boolean;
    style?: React.CSSProperties;
    labelStyle?: React.CSSProperties;
    [key: string]: any; // Allow data attributes and other props
}

/**
 * Custom toggle component with integrated label
 */
export const CustomToggle: React.FC<CustomToggleProps> = ({
    checked,
    onChange,
    label,
    disabled = false,
    style,
    labelStyle,
    ...restProps
}) => {
    const theme = useTheme();
    const [isFocused, setIsFocused] = useState(false);
    const normalizedLabel = typeof label === 'string' ? label.replace(/&nbsp;/g, '\u00A0') : label;

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent event from bubbling up to parent elements
        if (!disabled) {
            onChange(!checked);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!disabled) {
                onChange(!checked);
            }
        }
    };

    // Narrow enough that the label cannot sit beside the switch without being squeezed to one
    // character per line ("F/e/d"). Below this the label moves underneath and the switch halves,
    // so both stay legible. Hysteresis avoids flapping -- see useCompactLayout.
    const rootRef = useRef<HTMLDivElement>(null);
    const isCompact = useCompactLayout(rootRef, 120, 145);

    // Half-size switch geometry, so the knob still lines up with the track.
    const trackWidth = isCompact ? 22 : 44;
    const trackHeight = isCompact ? 12 : 24;
    const knobSize = isCompact ? 8 : 18;
    const knobInset = isCompact ? 1 : 2;

    return (
        <div
            ref={rootRef}
            {...restProps}
            tabIndex={disabled ? -1 : 0}
            role="checkbox"
            aria-checked={checked}
            aria-label={typeof label === 'string' ? label : undefined}
            style={{
                display: 'flex',
                flexDirection: isCompact ? 'column' : 'row',
                alignItems: 'center',
                gap: isCompact ? '2px' : theme.spacing.xs,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                // Stacked, the horizontal padding is width the label badly needs.
                padding: isCompact ? '2px' : `${theme.spacing.xs} ${theme.spacing.sm}`,
                border: `1px solid ${isFocused ? theme.colors.primary : theme.colors.border.default}`,
                borderRadius: theme.borderRadius.sm,
                backgroundColor: theme.colors.surface,
                outline: isFocused ? `2px solid ${theme.colors.primary}` : 'none',
                outlineOffset: '2px',
                ...style
            }}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0
                }}
            >
                <div
                    style={{
                        position: 'relative',
                        width: `${trackWidth}px`,
                        height: `${trackHeight}px`,
                        backgroundColor: checked ? theme.colors.primary : theme.colors.surfaceSecondary,
                        borderRadius: `${trackHeight / 2}px`,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        transition: `background-color ${theme.transitions.normal}`,
                        border: `1px solid ${checked ? theme.colors.primary : theme.colors.border.default}`,
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: `${knobInset}px`,
                            left: checked ? `${trackWidth - knobSize - knobInset}px` : `${knobInset}px`,
                            width: `${knobSize}px`,
                            height: `${knobSize}px`,
                            backgroundColor: theme.colors.surface,
                            borderRadius: '50%',
                            transition: `left ${theme.transitions.normal}`,
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        }}
                    />
                </div>
            </div>
            <span
                style={{
                    fontSize: theme.typography.fontSize.sm,
                    userSelect: 'none',
                    ...labelStyle,
                    ...(isCompact
                        ? {
                            // Stacked: the label gets the full width and centres under the switch,
                            // instead of being crushed into a one-character column.
                            fontSize: theme.typography.fontSize.xs,
                            textAlign: 'center' as const,
                            whiteSpace: 'normal' as const,
                            lineHeight: 1.2,
                        }
                        : {}),
                }}
            >
                {normalizedLabel}
            </span>
        </div>
    );
};
